import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { extractApiErrorMessage } from '@/lib/error-utils';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/hooks/use-toast';
import api from '@/lib/api';

interface UseDataConfig {
  endpoint: string;
  searchData?: any;
  pageSize?: number;
  initialFetch?: boolean;
  prefetch?: boolean;
  sumField?: string;
  useFilter?: boolean;
}

interface UseDataReturn<TData, TInput> {
  data: TData[];
  detail: any | null;
  isLoading: boolean;
  isDetailLoading: boolean;
  error: string | null;
  totalItems: number;
  currentPage: number;
  handlePageChange: (page: number) => Promise<void>;
  addItem: (data: TInput) => Promise<TData>;
  fetchDetail: (uuid: string) => Promise<TData | TData[]>;
  updateItem: (uuid: string, data: TInput) => Promise<TData>;
  deleteItem: (uuid: string) => Promise<void>;
  refreshData: () => Promise<void>;
  sumTotal: number | null;
  searchData: (filters: Record<string, any>, page?: number, pageSize?: number) => Promise<void>;
  clearSearch: () => void;
  isFiltered: boolean;
  isSearching: boolean;
  searchResults: TData[];
  searchTotalItems: number;
  searchCurrentPage: number;
  searchSumTotal: number | null;
}

interface FetchResult<TData> {
  results: TData[];
  count: number;
  sumValue: number | null;
}

interface PerformFetchOptions {
  method: 'GET' | 'POST';
  page?: number;
  pageSize?: number;
  filters?: Record<string, any>;
}

export const useCRUD = <TData extends { uuid: string } = any, TInput = Partial<TData>>(
  config: UseDataConfig,
  initialList?: TData[]
): UseDataReturn<TData, TInput> => {
  const [data, setData] = useState<TData[]>(initialList || []);
  const [detail, setDetail] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(config.initialFetch !== false);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  const { entity, setLoading: setGlobalLoading } = useAuth();
  const { endpoint, pageSize = 20 } = config;
  const prefetchEnabled = config.prefetch !== false;

  const { endpointPath, endpointParams } = useMemo(() => {
    const [rawPath, rawQuery = ''] = endpoint.split('?');
    const normalizedPath = rawPath.replace(/^\/+|\/+$/g, '');
    const parsedParams = Object.fromEntries(new URLSearchParams(rawQuery).entries());

    return {
      endpointPath: normalizedPath,
      endpointParams: parsedParams
    };
  }, [endpoint]);

  const stableSearchData = useMemo(() => config.searchData ?? {}, [JSON.stringify(config.searchData)]);
  const stableSumField = useMemo(() => config.sumField, [config.sumField]);
  const [sumTotal, setSumTotal] = useState<number | null>(null);

  const [searchResults, setSearchResults] = useState<TData[]>([]);
  const [searchTotalItems, setSearchTotalItems] = useState<number>(0);
  const [searchCurrentPage, setSearchCurrentPage] = useState<number>(0);
  const [isSearching] = useState<boolean>(false);
  const [searchSumTotal, setSearchSumTotal] = useState<number | null>(null);
  const [isFiltered, setIsFiltered] = useState<boolean>(false);
  const lastSearchFiltersRef = useRef<Record<string, any>>({});
  const lastSearchPageSizeRef = useRef<number | undefined>(undefined);

  const listPageCacheRef = useRef<Map<string, FetchResult<TData>>>(new Map());
  const searchPageCacheRef = useRef<Map<string, FetchResult<TData>>>(new Map());

  const cleanFilters = useCallback((filters: Record<string, any>) => {
    return Object.entries(filters).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== '' && value !== 'ALL') {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );
  }, []);

  const buildCacheKey = useCallback(
    ({ method, page = 0, pageSize: requestPageSize, filters = {} }: PerformFetchOptions) => {
      return JSON.stringify({
        entitySlug: entity?.slug,
        endpoint: endpointPath,
        method,
        page,
        pageSize: requestPageSize || pageSize,
        endpointParams,
        filters: method === 'GET' ? { ...endpointParams, ...stableSearchData } : cleanFilters(filters),
        sumField: stableSumField
      });
    },
    [cleanFilters, endpointParams, endpointPath, entity?.slug, pageSize, stableSearchData, stableSumField]
  );

  const clearPageCaches = useCallback(() => {
    listPageCacheRef.current.clear();
    searchPageCacheRef.current.clear();
  }, []);

  const performFetch = useCallback(
    async (options: PerformFetchOptions): Promise<FetchResult<TData> | null> => {
      const { method, page = 0, pageSize: requestPageSize, filters = {} } = options;

      if (!entity?.slug) return null;

      const isFilterEndpoint = method === 'POST';
      const url = isFilterEndpoint
        ? `/entities/${entity.slug}/erp/${endpointPath}/filter/`
        : `/entities/${entity.slug}/erp/${endpointPath}/`;

      const params: any = {
        ...endpointParams,
        page: page + 1,
        page_size: requestPageSize || pageSize
      };

      let response;
      if (method === 'GET') {
        if (stableSumField) {
          params.sum_field = stableSumField;
        }
        response = await api.get(url, {
          params: { ...params, ...stableSearchData }
        });
      } else {
        const body = cleanFilters(filters);
        if (stableSumField) {
          body.sum = [stableSumField];
        }
        response = await api.post(url, body, { params });
      }

      return {
        results: Array.isArray(response.data) ? response.data : response.data?.results || [],
        count: response.data?.count ?? 0,
        sumValue: stableSumField
          ? (response.data?.totals?.[stableSumField] ?? response.data?.[stableSumField] ?? null)
          : null
      };
    },
    [cleanFilters, endpointParams, endpointPath, entity?.slug, pageSize, stableSearchData, stableSumField]
  );

  const prefetchNextPage = useCallback(
    async (
      options: PerformFetchOptions,
      result: FetchResult<TData>,
      cacheRef: MutableRefObject<Map<string, FetchResult<TData>>>
    ) => {
      const resolvedPageSize = options.pageSize || pageSize;
      const totalPages = Math.ceil((result.count || 0) / resolvedPageSize);
      const nextPage = (options.page ?? 0) + 1;

      if (!entity?.slug || totalPages <= 1 || nextPage >= totalPages) {
        return;
      }

      const nextOptions: PerformFetchOptions = {
        ...options,
        page: nextPage,
        pageSize: resolvedPageSize
      };
      const nextKey = buildCacheKey(nextOptions);

      if (cacheRef.current.has(nextKey)) {
        return;
      }

      try {
        const nextResult = await performFetch(nextOptions);
        if (nextResult) {
          cacheRef.current.set(nextKey, nextResult);
        }
      } catch {
        return;
      }
    },
    [buildCacheKey, entity?.slug, pageSize, performFetch]
  );

  const useFilter = config.useFilter ?? false;

  const fetchData = useCallback(
    async (paginationParams?: { page?: number; pageSize?: number }, force = false) => {
      if (!entity?.slug) return;

      const requestOptions: PerformFetchOptions = {
        method: useFilter ? 'POST' : 'GET',
        page: paginationParams?.page ?? 0,
        pageSize: paginationParams?.pageSize,
        filters: useFilter ? stableSearchData : undefined
      };

      const cacheKey = buildCacheKey(requestOptions);
      const cachedResult = !force ? listPageCacheRef.current.get(cacheKey) : undefined;

      if (cachedResult) {
        setData(cachedResult.results);
        setTotalItems(cachedResult.count);
        setSumTotal(cachedResult.sumValue ?? 0);
        setError(null);
        if (prefetchEnabled) {
          void prefetchNextPage(requestOptions, cachedResult, listPageCacheRef);
        }
        return;
      }

      setIsLoading(true);
      try {
        const result = await performFetch(requestOptions);

        if (result) {
          listPageCacheRef.current.set(cacheKey, result);
          setData(result.results);
          setTotalItems(result.count);
          setSumTotal(result.sumValue ?? 0);
          if (prefetchEnabled) {
            void prefetchNextPage(requestOptions, result, listPageCacheRef);
          }
        }
        setError(null);
      } catch (error: any) {
        const errorMsg = extractApiErrorMessage({ data: error });
        setError(errorMsg);
        setData([]);
        setTotalItems(0);
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [buildCacheKey, entity?.slug, performFetch, prefetchEnabled, prefetchNextPage, stableSearchData, useFilter]
  );

  const fetchDetail = useCallback(
    async (uuid: string): Promise<TData | TData[]> => {
      if (!entity?.slug) return null as unknown as TData | TData[];
      if (!uuid) return null as unknown as TData | TData[];

      setIsDetailLoading(true);
      setGlobalLoading(true);
      try {
        const response = await api.get(`/entities/${entity.slug}/erp/${endpointPath}/${uuid}/`);
        setDetail(response.data);
        return response.data;
      } catch (error: any) {
        setDetail(null);
        const errorMsg = extractApiErrorMessage({ data: error });
        toast.error(errorMsg);
        return null as unknown as TData;
      } finally {
        setIsDetailLoading(false);
        setGlobalLoading(false);
      }
    },
    [entity?.slug, endpointPath]
  );

  const addItem = async (itemData: TInput): Promise<TData> => {
    if (!entity?.slug) throw new Error('Entity slug is required');
    setIsLoading(true);
    setGlobalLoading(true);

    try {
      const response = await api.post<TData>(`/entities/${entity.slug}/erp/${endpointPath}/`, itemData, {
        params: { ...endpointParams, ...stableSearchData }
      });
      clearPageCaches();
      setData(prev => [response.data, ...prev]);
      setError(null);
      return response.data;
    } catch (error: any) {
      const errorMsg = extractApiErrorMessage({ data: error });
      setError(errorMsg);
      throw error;
    } finally {
      setIsLoading(false);
      setGlobalLoading(false);
    }
  };

  const updateItem = async (uuid: string, itemData: TInput): Promise<TData> => {
    if (!entity?.slug) throw new Error('Entity slug is required');
    setIsLoading(true);
    setGlobalLoading(true);

    try {
      const response = await api.put<TData>(`/entities/${entity.slug}/erp/${endpointPath}/${uuid}/`, itemData, {
        params: { ...endpointParams, ...stableSearchData }
      });
      clearPageCaches();
      setData(prev => prev.map(item => (item.uuid === uuid ? response.data : item)));
      setError(null);
      return response.data;
    } catch (error: any) {
      const errorMsg = extractApiErrorMessage({ data: error });
      setError(errorMsg);
      throw error;
    } finally {
      setIsLoading(false);
      setGlobalLoading(false);
    }
  };

  const deleteItem = async (uuid: string | undefined): Promise<void> => {
    if (!entity?.slug) throw new Error('Entity slug is required');
    if (!uuid) return;
    setIsLoading(true);
    setGlobalLoading(true);

    try {
      await api.delete(`/entities/${entity.slug}/erp/${endpointPath}/${uuid}/`, {
        params: endpointParams
      });
      clearPageCaches();
      setData(prev => prev.filter(item => item.uuid !== uuid));
      setError(null);
    } catch (error: any) {
      const errorMsg = extractApiErrorMessage({ data: error });
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setGlobalLoading(false);
    }
  };

  const handlePageChange = useCallback(
    async (page: number) => {
      setCurrentPage(page);
      await fetchData({ page, pageSize });
    },
    [fetchData, pageSize]
  );

  const searchData = useCallback(
    async (filters: Record<string, any>, page: number = 0, searchPageSize?: number) => {
      if (!entity?.slug) return;

      lastSearchFiltersRef.current = filters;
      lastSearchPageSizeRef.current = searchPageSize;

      const requestOptions: PerformFetchOptions = {
        method: 'POST',
        page,
        pageSize: searchPageSize,
        filters
      };
      const cacheKey = buildCacheKey(requestOptions);
      const cachedResult = searchPageCacheRef.current.get(cacheKey);

      if (cachedResult) {
        setSearchResults(cachedResult.results);
        setSearchTotalItems(cachedResult.count);
        setSearchSumTotal(cachedResult.sumValue);
        setSearchCurrentPage(page);
        setIsFiltered(true);
        setError(null);
        if (prefetchEnabled) {
          void prefetchNextPage(requestOptions, cachedResult, searchPageCacheRef);
        }
        return;
      }

      setGlobalLoading(true);
      try {
        const result = await performFetch(requestOptions);

        if (result) {
          searchPageCacheRef.current.set(cacheKey, result);
          setSearchResults(result.results);
          setSearchTotalItems(result.count);
          setSearchSumTotal(result.sumValue);
          setSearchCurrentPage(page);
          setIsFiltered(true);
          if (prefetchEnabled) {
            void prefetchNextPage(requestOptions, result, searchPageCacheRef);
          }
        }
        setError(null);
      } catch (error: any) {
        setSearchResults([]);
        setSearchTotalItems(0);
        setSearchSumTotal(null);
        const errorMsg = extractApiErrorMessage({ data: error });
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setGlobalLoading(false);
      }
    },
    [buildCacheKey, entity?.slug, performFetch, prefetchEnabled, prefetchNextPage, setGlobalLoading]
  );

  const refreshData = useCallback(async (): Promise<void> => {
    listPageCacheRef.current.clear();
    searchPageCacheRef.current.clear();

    if (isFiltered) {
      await searchData(lastSearchFiltersRef.current, searchCurrentPage, lastSearchPageSizeRef.current);
      return;
    }

    await fetchData({ page: currentPage, pageSize }, true);
  }, [currentPage, fetchData, isFiltered, pageSize, searchCurrentPage, searchData]);

  const clearSearch = useCallback(() => {
    searchPageCacheRef.current.clear();
    setSearchResults([]);
    setSearchTotalItems(0);
    setSearchCurrentPage(0);
    setSearchSumTotal(null);
    setIsFiltered(false);
  }, []);

  useEffect(() => {
    clearPageCaches();
  }, [
    clearPageCaches,
    endpointParams,
    endpointPath,
    entity?.slug,
    pageSize,
    stableSearchData,
    stableSumField,
    useFilter
  ]);

  useEffect(() => {
    if (config.initialFetch !== false) {
      fetchData({ page: currentPage, pageSize });
    }
  }, [entity?.slug, fetchData, pageSize, config.initialFetch]);

  const displayData = isFiltered ? searchResults : data;
  const displayTotalItems = isFiltered ? searchTotalItems : totalItems;
  const displayCurrentPage = isFiltered ? searchCurrentPage : currentPage;
  const displaySumTotal = isFiltered ? searchSumTotal : sumTotal;

  return {
    data: displayData,
    detail,
    error,
    isLoading: isLoading || isSearching,
    isDetailLoading,
    totalItems: displayTotalItems,
    currentPage: displayCurrentPage,
    handlePageChange,
    addItem,
    fetchDetail,
    updateItem,
    deleteItem,
    refreshData,
    sumTotal: displaySumTotal,
    searchData,
    clearSearch,
    isFiltered,
    isSearching,
    searchResults,
    searchTotalItems,
    searchCurrentPage,
    searchSumTotal
  };
};
